/**
 * UsageSubscriptionCard — usage + subscription management card for ProfilePage.
 * Extracted verbatim from ProfilePage.jsx (god-file decomposition).
 */

import React, { useState, useEffect } from 'react';
import PressableScale from './motion/PressableScale';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { PLAN_META } from '../../utils/planMeta';

export default function UsageSubscriptionCard({ isAdmin, queryUsage, subscriptionPlan, subscriptionInfo, adminPreviewPlan, onManage, onUpgrade }) {
    const meta = PLAN_META[subscriptionPlan] || PLAN_META.FREE;
    const isPaid = subscriptionPlan && subscriptionPlan !== 'FREE';
    const cancelPending = subscriptionInfo?.cancelAtPeriodEnd === true;
    const isPastDue = subscriptionInfo?.status === 'past_due';
    const renewalDate = subscriptionInfo?.currentPeriodEnd
        ? new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;
    const cancelDate = subscriptionInfo?.cancelAt
        ? new Date(subscriptionInfo.cancelAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : renewalDate;

    const used  = queryUsage.queriesUsed  ?? 0;
    const limit = queryUsage.queryLimit   ?? 0;
    const isUnlimited = isAdmin || limit === 'unlimited' || limit === -1 || subscriptionPlan === 'ULTIMATE';
    const remaining = isUnlimited ? Infinity : Math.max(0, limit - used);
    const pct = isUnlimited ? 0 : Math.min((used / Math.max(limit, 1)) * 100, 100);
    const isAtLimit  = !isUnlimited && used >= limit;
    const isNearLimit = !isUnlimited && used >= limit * 0.8 && used < limit;
    const barColors = isAtLimit
        ? ['#f87171', '#ef4444']
        : isNearLimit
            ? ['#fbbf24', '#f59e0b']
            : [meta.color + 'cc', meta.color];

    return (
        <View style={[usc.card, { borderColor: meta.color + '33' }]}>
            {/* ── Top: plan badge left, action button right ── */}
            <View style={usc.topRow}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <View style={[usc.planBadge, { backgroundColor: meta.color + '20', borderColor: meta.color + '50' }]}>
                        <Text style={[usc.planBadgeText, { color: meta.color }]}>
                            {meta.emoji ? `${meta.emoji} ` : ''}{meta.label} Plan
                        </Text>
                    </View>
                    {adminPreviewPlan && <View style={usc.pill}><Text style={usc.pillText}>PREVIEW</Text></View>}
                    {isPastDue    && <View style={[usc.pill, { backgroundColor: 'rgba(248,113,113,0.15)', borderColor: 'rgba(248,113,113,0.35)' }]}><Text style={[usc.pillText, { color: '#f87171' }]}>PAYMENT FAILED</Text></View>}
                    {cancelPending && !isPastDue && <View style={[usc.pill, { backgroundColor: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.3)' }]}><Text style={[usc.pillText, { color: '#F59E0B' }]}>CANCELS {cancelDate || 'SOON'}</Text></View>}
                </View>
                {isPaid ? (
                    <PressableScale style={usc.manageBtn} onPress={onManage}>
                        <Text style={usc.manageBtnText}>Manage</Text>
                    </PressableScale>
                ) : (
                    <PressableScale style={[usc.manageBtn, usc.upgradeBtn]} onPress={onUpgrade}>
                        <Text style={[usc.manageBtnText, { color: '#a78bfa' }]}>Upgrade →</Text>
                    </PressableScale>
                )}
            </View>

            {/* ── Middle: big numbers ── */}
            <View style={usc.numbersRow}>
                {isUnlimited ? (
                    <View>
                        <Text style={usc.remainingNum}>∞</Text>
                        <Text style={usc.remainingLabel}>queries remaining</Text>
                    </View>
                ) : (
                    <View>
                        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
                            <Text style={[usc.remainingNum, isAtLimit && { color: '#f87171' }, isNearLimit && { color: '#fbbf24' }]}>
                                {remaining.toLocaleString()}
                            </Text>
                            <Text style={usc.remainingLabel}>remaining this month</Text>
                        </View>
                        <Text style={usc.usedLine}>{used.toLocaleString()} of {limit.toLocaleString()} used</Text>
                    </View>
                )}
            </View>

            {/* ── Progress bar ── */}
            {!isUnlimited && (
                <View style={usc.barBg}>
                    <LinearGradient
                        colors={barColors}
                        start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                        style={[usc.barFill, { width: `${pct}%` }]}
                    />
                </View>
            )}

            {/* ── Bottom: renewal / status info ── */}
            {(isPaid && renewalDate && !cancelPending && !isPastDue) && (
                <Text style={usc.footnote}>Renews {renewalDate}</Text>
            )}
            {cancelPending && (
                <Text style={[usc.footnote, { color: '#F59E0B' }]}>Access continues until {cancelDate}</Text>
            )}
            {isAtLimit && (
                <Text style={[usc.footnote, { color: '#f87171' }]}>Monthly limit reached — resets next billing cycle</Text>
            )}

            {/* ── Past-due banner ── */}
            {isPastDue && (
                <PressableScale style={usc.pastDueBanner} onPress={onManage}>
                    <Text style={usc.pastDueText}>Update payment method to keep your subscription active →</Text>
                </PressableScale>
            )}
        </View>
    );
}

const usc = StyleSheet.create({
    card:          { borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.035)', padding: 18, marginBottom: 12, gap: 14 },
    topRow:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    planBadge:     { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, borderWidth: 1 },
    planBadgeText: { fontSize: 13, fontWeight: '700', fontFamily: 'Slate' },
    pill:          { backgroundColor: 'rgba(167,139,250,0.15)', borderWidth: 1, borderColor: 'rgba(167,139,250,0.3)', paddingHorizontal: 7, paddingVertical: 2, borderRadius: 5 },
    pillText:      { color: '#a78bfa', fontSize: 9, fontWeight: '700', letterSpacing: 0.3 },
    manageBtn:     { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 9, borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', backgroundColor: 'rgba(255,255,255,0.06)' },
    manageBtnText: { color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: '600', fontFamily: 'Slate' },
    upgradeBtn:    { borderColor: 'rgba(167,139,250,0.4)', backgroundColor: 'rgba(167,139,250,0.12)' },
    numbersRow:    { paddingVertical: 2 },
    remainingNum:  { color: '#ffffff', fontSize: 36, fontWeight: '800', fontFamily: 'Slate', lineHeight: 40 },
    remainingLabel:{ color: 'rgba(255,255,255,0.45)', fontSize: 14, fontWeight: '500', fontFamily: 'Slate' },
    usedLine:      { color: 'rgba(255,255,255,0.3)', fontSize: 12, fontFamily: 'Slate', marginTop: 3 },
    barBg:         { height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
    barFill:       { height: 6, borderRadius: 3 },
    footnote:      { color: 'rgba(255,255,255,0.3)', fontSize: 11, fontFamily: 'Slate' },
    pastDueBanner: { backgroundColor: 'rgba(248,113,113,0.1)', borderWidth: 1, borderColor: 'rgba(248,113,113,0.25)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
    pastDueText:   { color: '#f87171', fontSize: 12, fontWeight: '600', textAlign: 'center', fontFamily: 'Slate' },
});
